import * as Sentry from "@sentry/react";
import axios from "axios";
import saveAs from "file-saver";
import _, { isError } from "lodash";
import { separateByComma } from "src/utility/utilityFunctions";

import { createSlice } from "@reduxjs/toolkit";
import { query } from "@services/api";

import { buildFilters, handleErrors } from "../../../api/apiFunctions";
import { axiosGetNoFormat, axiosGetWithNext } from "../../../api/axiosCalls";
import { setError } from "../errorSlice";
import {
  resetStepperValue,
  setIsStepper,
  startGlobalLoad,
  stopGlobalLoad,
  updateStepperValue,
} from "../globalLoadSlice";
import {
  buildInvoicingReportQuery,
  buildOrderVariantSummaryQuery,
  buildReportByIdQuery,
  buildRoadmapForecastQuery,
  buildVariantAllocationQuery,
} from "./helpers";
import {
  mapAvailableInventoryItems,
  mapCustomCouponReport,
  mapFinanceBillingReport,
  mapFinanceJournalReport,
  mapInventoryItems,
  mapInvoiceDetailReport,
  mapInvoiceDoubleCheckReport,
  mapInvoicingReport,
  mapOpenPurchaseOrderReport,
  mapOrderVariantSummary,
  mapOrderWindowDetail,
  mapOrderWindowItems,
  mapOrderWindowSummaryPresentation,
  mapOrderedItemsReport,
  mapPOComplianceItems,
  mapPreOrderNonParticipantReport,
  mapPrintInvoiceReport,
  mapPurchaseOrderReport,
  mapRFPSummary,
  mapRoadmapForecastItems,
  mapShipListReport,
  mapSupplierSpendDetailReport,
  mapSupplierSpendSummaryReport,
  mapVariantAllocationReport,
} from "./maps";

function reportError(err) {
  if (isError(err)) {
    console.error(err);
    Sentry.captureException(err);
  }
}

const initialState = {
  isLoading: false,
  reportType: null,
  reportData: [],
  error: null,
};

const reportSlice = createSlice({
  name: "reports",
  initialState,
  reducers: {
    setIsLoading(state) {
      state.isLoading = true;
    },
    getReportsSuccess(state, action) {
      const { type, reportData } = action.payload;
      state.reportType = type;
      state.reportData = reportData;
      state.error = null;
      state.isLoading = false;
    },
    clearReports(state) {
      state.isLoading = false;
      state.reportType = null;
      state.reportData = [];
      state.error = null;
    },
    setFailure: (state, action) => {
      const { error } = action.payload;
      state.isLoading = false;
      state.error = error;
    },
  },
});

export const { setIsLoading, clearReports, setFailure } = reportSlice.actions;

const { getReportsSuccess: storeReportsSuccess } = reportSlice.actions;

// Reports whose endpoint returns a ready-made CSV string instead of rows.
const CSV_STRING_REPORT_TYPES = ["audit-report", "calendar-report"];
// The only report stored as a grouped object instead of an array of rows.
const OBJECT_REPORT_TYPE = "order-window-summary-presentation";

/**
 * Validating wrapper around the slice action. It throws when called, i.e.
 * inside each thunk's try/catch, so a malformed response (e.g. an HTML error
 * page) surfaces through the existing failure path instead of corrupting the
 * store. CSV-string reports must be strings, the order-window summary
 * presentation must be an object, and every other report must be an array.
 *
 * @param {{ type: string, reportData: unknown }} payload
 */
export function getReportsSuccess(payload) {
  const { type, reportData } = payload;

  let isValid;
  if (CSV_STRING_REPORT_TYPES.includes(type)) {
    isValid = typeof reportData === "string";
  } else if (type === OBJECT_REPORT_TYPE) {
    isValid = _.isPlainObject(reportData);
  } else {
    isValid = Array.isArray(reportData);
  }

  if (!isValid) {
    throw new Error(
      `Report "${type}" returned an unexpected response (received ${
        reportData === null ? "null" : typeof reportData
      }).`
    );
  }
  return storeReportsSuccess(payload);
}

export default reportSlice.reducer;

export const getInventoryItemReport =
  (filterObject, isAvailableInventory = false) =>
  async (dispatch) => {
    try {
      dispatch(setIsLoading());
      dispatch(startGlobalLoad());
      const queryString = `/api/inventory-report?${query(filterObject)}`;
      const response = await axiosGetNoFormat(queryString);
      if (response.error) throw response.error;
      const mappedData = isAvailableInventory
        ? mapAvailableInventoryItems(response.data)
        : mapInventoryItems(response.data);
      dispatch(
        getReportsSuccess({
          type: isAvailableInventory ? "available-inventory" : "inventory",
          reportData: mappedData,
        })
      );
      dispatch(stopGlobalLoad());
    } catch (err) {
      reportError(err);
      dispatch(setFailure({ error: err.toString() }));
      dispatch(setError({ error: err.toString(), source: "Reports" }));
      dispatch(stopGlobalLoad());
    }
  };

export const getOrderVariantSummaryReport =
  (filterObject) => async (dispatch) => {
    try {
      dispatch(setIsLoading());
      dispatch(
        setIsStepper({
          stepBool: true,
          stepTitle: "Generating Finance Tracker Report",
        })
      );
      let ovArray = [];
      let stepValue = 15;

      const queryString = buildOrderVariantSummaryQuery(filterObject);
      const initialResponse = await axiosGetWithNext(queryString);
      if (initialResponse.error) {
        throw initialResponse.error;
      }
      const initialMappedData = mapOrderVariantSummary(
        initialResponse.data.data
      );
      ovArray = ovArray.concat(initialMappedData);

      let nextLink = initialResponse.data.nextLink;
      dispatch(updateStepperValue({ value: stepValue }));

      if (nextLink) {
        const fetchCount = Math.ceil(
          (initialResponse.data.totalEntries - 100) / 100
        );
        stepValue = parseFloat((85 / fetchCount).toFixed(2));

        for (let i = 0; i < fetchCount; i++) {
          const response = await axiosGetWithNext(nextLink);
          if (response.error) throw response.error;
          nextLink = response.data.nextLink;
          const mappedData = mapOrderVariantSummary(response.data.data);
          ovArray = ovArray.concat(mappedData);
          dispatch(updateStepperValue({ value: stepValue }));
        }
      } else {
        dispatch(updateStepperValue({ value: 75 }));
      }
      dispatch(
        getReportsSuccess({ type: "finance-tracker", reportData: ovArray })
      );
      dispatch(resetStepperValue());
    } catch (err) {
      reportError(err);
      dispatch(setFailure({ error: err.toString() }));
      dispatch(setError({ error: err.toString(), source: "Reports" }));
      dispatch(resetStepperValue());
    }
  };

export const getPOComplianceReport = (filterObject) => async (dispatch) => {
  try {
    dispatch(setIsLoading());
    dispatch(startGlobalLoad());
    const queryString = `/api/po-compliance-report?${query({ filter: filterObject })}`;
    const response = await axiosGetNoFormat(queryString);
    if (response.error) throw response.error;
    const mappedData = mapPOComplianceItems(response.data);
    dispatch(
      getReportsSuccess({
        type: "po-compliance",
        reportData: mappedData,
      })
    );
    dispatch(stopGlobalLoad());
  } catch (err) {
    reportError(err);
    dispatch(setFailure({ error: err.toString() }));
    dispatch(setError({ error: err.toString(), source: "Reports" }));
    dispatch(stopGlobalLoad());
  }
};

export const getRoadmapForecastReport = (filterObject) => async (dispatch) => {
  try {
    dispatch(setIsLoading());
    dispatch(startGlobalLoad());
    const queryString = buildRoadmapForecastQuery(filterObject);
    const response = await axiosGetNoFormat(queryString);
    if (response.error) throw response.error;
    const mappedData = mapRoadmapForecastItems(response.data);
    dispatch(
      getReportsSuccess({
        type: "roadmap-forecast",
        reportData: mappedData,
      })
    );
    dispatch(stopGlobalLoad());
  } catch (err) {
    reportError(err);
    dispatch(setFailure({ error: err.toString() }));
    dispatch(setError({ error: err.toString(), source: "Reports" }));
    dispatch(stopGlobalLoad());
  }
};

export const getCustomCouponReport = (filterObject) => async (dispatch) => {
  try {
    dispatch(setIsLoading());
    dispatch(startGlobalLoad());
    const queryString = `/api/custom-coupon-report?filter[approved-at-range]=${filterObject.dateRange}&filter[channel]=${filterObject.channel}`;
    const response = await axiosGetNoFormat(queryString);
    if (response.error) throw response.error;
    const mappedData = mapCustomCouponReport(response.data);
    dispatch(
      getReportsSuccess({
        type: "coupon",
        reportData: mappedData,
      })
    );
    dispatch(stopGlobalLoad());
  } catch (err) {
    reportError(err);
    dispatch(setFailure({ error: err.toString() }));
    dispatch(setError({ error: err.toString(), source: "Reports" }));
    dispatch(stopGlobalLoad());
  }
};

export const getCouponProductionReport =
  (freeTypeFields, callback) => async (dispatch, getState) => {
    const filters = getState().filters;
    const channel = getState().user.currentChannel;
    const orderTypes = [
      "on-demand",
      "from-inventory",
      "to-inventory",
      "pre-order",
    ];

    try {
      const orderTypeFilter = orderTypes.includes(filters.type)
        ? { orderType: filters.type }
        : { orderCalendarMonthIds: filters.type };

      const params = {
        filter: {
          ..._.mapValues(freeTypeFields, (val) => val || null),
          ...orderTypeFilter,
          orderDateRange: `${filters.fromDate} - ${filters.toDate}`,
          channel: channel,
          anaplanProgramType: filters.anaplanProgramType,
          userIds: separateByComma(filters.user, "id"),
          brandIds: separateByComma(filters.brand, "id"),
          addressIds: separateByComma(filters.address, "id"),
          programIds: separateByComma(filters.program, "id"),
          itemTypeIds: separateByComma(filters.itemType, "id"),
          businessUnitNames: separateByComma(filters.bu, "name"),
          stateIds: separateByComma(filters.stateIds, "id"),
          territoryIds: separateByComma(filters.territory, "id"),
          supplierIds: separateByComma(filters.supplier, "id"),
        },
      };
      dispatch(setIsLoading());
      dispatch(startGlobalLoad());
      const queryString = `/api/coupon-production-report?${query(params)}`;
      const response = await axiosGetNoFormat(queryString);
      if (response.error) throw response.error;
      if (response.data.length === 0)
        throw new Error("No data found for your search.");

      const blob = new Blob([response.data], {
        type: "text/csv;charset=utf-8",
      });
      saveAs(blob, `compliance_audit_report.csv`);
      dispatch(stopGlobalLoad());
    } catch (err) {
      reportError(err);
      dispatch(setFailure({ error: err.toString() }));
      dispatch(setError({ error: err.toString(), source: "Reports" }));
      dispatch(stopGlobalLoad());
    } finally {
      callback?.();
    }
  };

export const getReportById = (id, filterObject) => async (dispatch) => {
  const reportMapReference = {
    "order-window-summary-presentation": mapOrderWindowSummaryPresentation,
    "order-window-detail": mapOrderWindowDetail,
    "rfp-summary": mapRFPSummary,
  };

  try {
    dispatch(setIsLoading());
    dispatch(startGlobalLoad());
    const queryString = buildReportByIdQuery(id, filterObject);
    const response = await axiosGetNoFormat(queryString);
    if (response.error) throw response.error;
    const mappedData = reportMapReference[id](
      response.data,
      filterObject.isNational ? "national" : "territory"
    );
    dispatch(getReportsSuccess({ type: id, reportData: mappedData }));
    dispatch(stopGlobalLoad());
  } catch (err) {
    reportError(err);
    dispatch(setFailure({ error: err.toString() }));
    dispatch(setError({ error: err.toString(), source: "Reports" }));
    dispatch(stopGlobalLoad());
  }
};

export const getShipListReport = (filterObject) => async (dispatch) => {
  try {
    dispatch(setIsLoading());
    dispatch(startGlobalLoad());
    const queryString = `/api/supplier-shipping-report?filter[purchase-order-ids]=${filterObject.poIds}`;
    const response = await axiosGetNoFormat(queryString);
    if (response.error) throw response.error;
    const mappedData = mapShipListReport(response.data);
    dispatch(getReportsSuccess({ type: "ship-list", reportData: mappedData }));
    dispatch(stopGlobalLoad());
  } catch (err) {
    reportError(err);
    dispatch(setFailure({ error: err.toString() }));
    dispatch(setError({ error: err.toString(), source: "Reports" }));
    dispatch(stopGlobalLoad());
  }
};

export const getSupplierSpendReportById =
  (id, filterObject) => async (dispatch) => {
    const reportMapReference = {
      "supplier-summary": mapSupplierSpendSummaryReport,
      "supplier-detail": mapSupplierSpendDetailReport,
    };

    try {
      dispatch(setIsLoading());
      dispatch(startGlobalLoad());
      const rangeName =
        filterObject.dateRangeType === "po_submitted"
          ? "submitted-at-range"
          : "in-market-range";
      const queryString = buildFilters(
        filterObject,
        `filter[${rangeName}]=${filterObject.dateRange}`,
        "",
        `/api/supplier-spend-reports/${id.split("-")[1]}`,
        "reports"
      );
      const response = await axiosGetNoFormat(queryString);
      if (response.error) throw response.error;
      const mappedData = reportMapReference[id](response.data);
      dispatch(getReportsSuccess({ type: id, reportData: mappedData }));
      dispatch(stopGlobalLoad());
    } catch (err) {
      reportError(err);
      dispatch(setFailure({ error: err.toString() }));
      dispatch(setError({ error: err.toString(), source: "Reports" }));
      dispatch(stopGlobalLoad());
    }
  };

export const getFinanceReportByType =
  (filterObject, type) => async (dispatch) => {
    try {
      dispatch(setIsLoading());
      dispatch(startGlobalLoad());
      let queryString = `/api/billing-report?filter[actual-ship-date]=${filterObject.dateRange}&filter[channel]=${filterObject.channel}`;
      if (filterObject.role !== "all") {
        queryString += `&filter[is-purchaser-select]=${
          filterObject.role === "select" ? "true" : "false"
        }`;
      }
      const response = await axiosGetNoFormat(queryString);
      if (response.error) throw response.error;
      const mappedData =
        type === "finance-billing"
          ? mapFinanceBillingReport(response.data)
          : mapFinanceJournalReport(response.data);
      dispatch(getReportsSuccess({ type: type, reportData: mappedData }));
      dispatch(stopGlobalLoad());
    } catch (err) {
      reportError(err);
      dispatch(setFailure({ error: err.toString() }));
      dispatch(setError({ error: err.toString(), source: "Reports" }));
      dispatch(stopGlobalLoad());
    }
  };

export const getOrderedItemsReport = (filterObject) => async (dispatch) => {
  try {
    dispatch(setIsLoading());
    dispatch(startGlobalLoad());
    const queryString = buildFilters(
      filterObject,
      "",
      "",
      "/api/ordered-items-report",
      "reports"
    );
    const response = await axiosGetNoFormat(queryString);
    if (response.error) throw response.error;
    const mappedData = mapOrderedItemsReport(response.data);
    dispatch(
      getReportsSuccess({ type: "ordered-items", reportData: mappedData })
    );
    dispatch(stopGlobalLoad());
  } catch (err) {
    reportError(err);
    dispatch(setFailure({ error: err.toString() }));
    dispatch(setError({ error: err.toString(), source: "Reports" }));
    dispatch(stopGlobalLoad());
  }
};

export const getOrderWindowItemsReport = (filterObject) => async (dispatch) => {
  try {
    dispatch(setIsLoading());
    dispatch(startGlobalLoad());
    const queryString = buildFilters(
      filterObject,
      "",
      "",
      "/api/order-window-items-report",
      "reports"
    );
    const response = await axiosGetNoFormat(queryString);
    if (response.error) throw response.error;
    const mappedData = mapOrderWindowItems(response.data);

    dispatch(
      getReportsSuccess({
        type: "order-window-items",
        reportData: mappedData,
      })
    );
    dispatch(stopGlobalLoad());
  } catch (err) {
    reportError(err);
    dispatch(setFailure({ error: err.toString() }));
    dispatch(setError({ error: err.toString(), source: "Reports" }));
    dispatch(stopGlobalLoad());
  }
};

export const getInvoicingReport = (filterObject) => async (dispatch) => {
  try {
    dispatch(setIsLoading());
    dispatch(startGlobalLoad());
    const queryString = buildInvoicingReportQuery(filterObject);
    const response = await axiosGetNoFormat(queryString);
    if (response.error) throw response.error;
    const mappedData = mapInvoicingReport(response.data);
    dispatch(getReportsSuccess({ type: "invoicing", reportData: mappedData }));
    dispatch(stopGlobalLoad());
  } catch (err) {
    reportError(err);
    dispatch(setFailure({ error: err.toString() }));
    dispatch(setError({ error: err.toString(), source: "Reports" }));
    dispatch(stopGlobalLoad());
  }
};

export const getPrintInvoiceReport = (invoiceNumber) => async (dispatch) => {
  try {
    dispatch(setIsLoading());
    dispatch(startGlobalLoad());
    const response = await axiosGetNoFormat(
      `/api/print-invoice-report?invoice-number=${invoiceNumber}`
    );
    if (response.error) throw new Error(response.error);
    if (response.data.length === 5)
      throw new Error(`No results found for invoice # ${invoiceNumber}`);
    const mappedData = mapPrintInvoiceReport(response.data);
    dispatch(
      getReportsSuccess({ type: "print-invoice", reportData: mappedData })
    );
    dispatch(stopGlobalLoad());
  } catch (err) {
    reportError(err);
    dispatch(setFailure({ error: err.toString() }));
    dispatch(setError({ error: err.toString(), source: "Reports" }));
    dispatch(stopGlobalLoad());
  }
};

export const getInvoiceDoubleCheckReport =
  (invoiceNumber) => async (dispatch) => {
    try {
      dispatch(setIsLoading());
      dispatch(startGlobalLoad());
      const response = await axiosGetNoFormat(
        `/api/invoice-double-check-report?invoice-number=${invoiceNumber}`
      );
      if (response.error) throw response.error;
      const mappedData = mapInvoiceDoubleCheckReport(response.data);
      dispatch(
        getReportsSuccess({
          type: "invoice-double-check",
          reportData: mappedData,
        })
      );
      dispatch(stopGlobalLoad());
    } catch (err) {
      reportError(err);
      dispatch(setFailure({ error: err.toString() }));
      dispatch(setError({ error: err.toString(), source: "Reports" }));
      dispatch(stopGlobalLoad());
    }
  };

export const getOpenPurchaseOrdersReport =
  (filterObject) => async (dispatch) => {
    try {
      dispatch(setIsLoading());
      dispatch(startGlobalLoad());
      const queryString = buildFilters(
        filterObject,
        "",
        "",
        "/api/open-purchase-order-report",
        "reports"
      );
      const response = await axiosGetNoFormat(queryString);
      if (response.error) throw response.error;
      const mappedData = mapOpenPurchaseOrderReport(response.data);
      dispatch(
        getReportsSuccess({
          type: "open-purchase-orders",
          reportData: mappedData,
        })
      );
      dispatch(stopGlobalLoad());
    } catch (err) {
      reportError(err);
      dispatch(setFailure({ error: err.toString() }));
      dispatch(setError({ error: err.toString(), source: "Reports" }));
      dispatch(stopGlobalLoad());
    }
  };

export const getInvoiceDetailReport = (invoiceNumber) => async (dispatch) => {
  try {
    dispatch(setIsLoading());
    dispatch(startGlobalLoad());
    const response = await axiosGetNoFormat(
      `/api/invoice-detail-report?invoice-number=${invoiceNumber}`
    );
    if (response.error) throw response.error;
    const mappedData = mapInvoiceDetailReport(response.data);
    dispatch(
      getReportsSuccess({
        type: "invoice-detail",
        reportData: mappedData,
      })
    );
    dispatch(stopGlobalLoad());
  } catch (err) {
    reportError(err);
    dispatch(setFailure({ error: err.toString() }));
    dispatch(setError({ error: err.toString(), source: "Reports" }));
    dispatch(stopGlobalLoad());
  }
};

export const getVariantAllocationReport =
  (filterObject) => async (dispatch) => {
    try {
      dispatch(setIsLoading());
      dispatch(startGlobalLoad());
      const queryString = buildVariantAllocationQuery(filterObject);
      const response = await axiosGetNoFormat(queryString);
      if (response.error) throw response.error;
      const mappedData = mapVariantAllocationReport(response.data);
      dispatch(
        getReportsSuccess({
          type: "variant-allocation",
          reportData: mappedData,
        })
      );
      dispatch(stopGlobalLoad());
    } catch (err) {
      reportError(err);
      dispatch(setFailure({ error: err.toString() }));
      dispatch(setError({ error: err.toString(), source: "Reports" }));
      dispatch(stopGlobalLoad());
    }
  };

export const getPurchaseOrderReport = (filterObject) => async (dispatch) => {
  try {
    dispatch(setIsLoading());
    dispatch(startGlobalLoad());
    const queryString = buildFilters(
      filterObject,
      "",
      "",
      "/api/purchase-order-report",
      "purchase-order-report"
    );
    const response = await axiosGetNoFormat(queryString);
    if (response.error) throw response.error;
    const mappedData = mapPurchaseOrderReport(response.data);
    dispatch(
      getReportsSuccess({
        type: "purchase-order-report",
        reportData: mappedData,
      })
    );
    dispatch(stopGlobalLoad());
  } catch (err) {
    reportError(err);
    dispatch(setFailure({ error: err.toString() }));
    dispatch(setError({ error: err.toString(), source: "Reports" }));
    dispatch(stopGlobalLoad());
  }
};

export const getAuditReport = (filterObject) => async (dispatch) => {
  try {
    dispatch(setIsLoading());
    dispatch(startGlobalLoad());
    const queryString = buildFilters(
      filterObject,
      "",
      "",
      "/api/audit-report",
      "reports"
    );
    const response = await axios.get(queryString);
    if (response.error) throw response.error;
    dispatch(
      getReportsSuccess({
        type: "audit-report",
        reportData: response.data,
      })
    );
    dispatch(stopGlobalLoad());
  } catch (err) {
    reportError(err);
    const error = handleErrors(err);
    dispatch(setFailure({ error }));
    dispatch(setError({ error, source: "Audit Report" }));
    dispatch(stopGlobalLoad());
  }
};

export const getCalendarReport = (filterObject) => async (dispatch) => {
  try {
    dispatch(setIsLoading());
    dispatch(startGlobalLoad());
    const queryString = buildFilters(
      filterObject,
      "",
      "",
      "/api/calendar-report",
      "reports"
    );
    const response = await axios.get(queryString);
    if (response.error) throw response.error;
    dispatch(
      getReportsSuccess({
        type: "calendar-report",
        reportData: response.data,
      })
    );
    dispatch(stopGlobalLoad());
  } catch (err) {
    reportError(err);
    dispatch(setFailure({ error: err.toString() }));
    dispatch(setError({ error: err.toString(), source: "Audit Report" }));
    dispatch(stopGlobalLoad());
  }
};

export const getPreOrderNonParticipantReport =
  (filterObject) => async (dispatch) => {
    try {
      dispatch(setIsLoading());
      dispatch(startGlobalLoad());
      const queryString = `/api/pre-order-non-participant-report?${query(
        filterObject
      )}`;
      const response = await axiosGetNoFormat(queryString);
      if (response.error) throw response.error;
      const mappedData = mapPreOrderNonParticipantReport(response.data);
      dispatch(
        getReportsSuccess({
          type: "pre-order-non-participant",
          reportData: mappedData,
        })
      );
      dispatch(stopGlobalLoad());
    } catch (err) {
      reportError(err);
      dispatch(setFailure({ error: err.toString() }));
      dispatch(setError({ error: err.toString(), source: "Reports" }));
      dispatch(stopGlobalLoad());
    }
  };
